<template>
    <div class="data-status-grid">
        <loading-spinner :show-loading="showLoading"></loading-spinner>
        <b-filter-modal
            v-model="isShowFilterEditor"
            :filterOptions="filterOptions"
            :filterableFields="filterableFields"
            @filterChanged="applyFilters"></b-filter-modal>
        <information-modal
            title="Unable to download file"
            :body="downloadErrorBody"
            v-model="showDownloadErrorModal">
        </information-modal>
        <b-table-header
            :value="options"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100"
            :current-item-count="total">
            <template #default>
                <button
                    @click="showFilterEditor($event)"
                    class="btn btn-primary filter-button-width">
                    <i class="bi bi-search pe-1"></i>
                    Filter
                </button>
                <slot name="header.toolbar"></slot>
                <div
                    class="filter-description"
                    v-if="hasAppliedFilters">
                    <span>
                        {{ filtersDescription }}
                    </span>
                    <button
                        class="btn-close"
                        v-tooltip="'Remove all currently applied filters.'"
                        @click="resetFilters()"></button>
                </div>
            </template>
            <template #headerTopBar>
                <queue-pause-button
                    :show-pause-resume-button="showQueueStatus"
                    class=""></queue-pause-button>
                <div
                    class="btn-group ms-auto"
                    style=""
                    role="group"
                    v-if="props.showWarningDetails">
                    <drop-down
                        label="Error/Warning Details"
                        :item-count="2"
                        :is-primary-button="false">
                        <template v-slot:item1="{}">
                            <a
                                class="dropdown-item"
                                :href="`${store.errorDescriptionsDownloadUrl}csv`"
                                >Download as .csv</a
                            >
                        </template>
                        <template v-slot:item2="{}">
                            <a
                                class="dropdown-item"
                                :href="`${store.errorDescriptionsDownloadUrl}xlsx`"
                                >Download as .xlsx</a
                            >
                        </template>
                    </drop-down>
                </div>
            </template>
        </b-table-header>
        <b-table
            :items="result"
            :columns="columns"
            :caption="'List of errors and warnings'"
            :value="options"
            @optionChange="handleOptionChange"
            class="mt-1">
            <template v-slot:cell.fileName="{item, column}">
                <button
                    class="btn btn-link ms-0 ps-0 text-start"
                    v-if="item.downloadUrl != null && item.downloadUrl != ''"
                    @click.prevent="downloadFileHopefully(item.downloadUrl, item.fileName)">
                    {{ item.fileName }}
                </button>
                <span v-else>{{ item.fileName }}</span>
            </template>
            <template #cell.status="{item}">
                <button
                    class="btn btn-link ms-0 ps-0 text-start"
                    @click.prevent="redirectToUrl(getDetailsUrl(item.id))"
                    v-if="isStatusLinkEnabled(item)">
                    {{ item.status }}
                </button>
                <span v-else>{{ item.status }}</span>
            </template>
        </b-table>
        <b-table-header
            :value="options"
            @optionChange="handleOptionChange"
            header-slot-class="b-header-slot w-100"
            :current-item-count="total">
        </b-table-header>
    </div>
</template>
<script setup lang="ts">
import BTable from "./BTable/BTable.vue";
import BTableHeader from "./BTable/BTableHeader.vue";
import {BTableColumn, BTableOptions} from "./BTable/BTable.types";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {describeFilters, BFilterOptions, BFilter, BFilterableField} from "./BFilterEditor/BFilterEditor.types";

import BFilterModal from "./BFilterEditor/BFilterModal.vue";
import {handleErrorInResponse, getFiltersFromString, getSourceNumberFromText} from "@/utils";
import {debounce} from "vue-debounce";
import QueuePauseButton from "./QueuePauseButton.vue";
import {computed, ref, watch, defineProps} from "vue";
import {UploadType} from "@/types";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useUofStore} from "@/stores/uofStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {usePopulationStore} from "@/stores/populationStore";
import {AxiosResponse} from "axios";
import InformationModal from "./Misc/InformationModal.vue";
import DropDown from "@/components/Misc/DropDown.vue";
import { useDcraStore } from "@/stores/dcraStore";

const props = defineProps<{
    title: string;
    refreshRequired: string;
    dataset: string;
    onlyHasOneOri: boolean;
    showQueueStatus: boolean;
    submissionPeriodHidden: boolean;
    showWarningDetails: boolean;
    noTotal: boolean;
}>();

var store = {} as any;
var uploadType:UploadType = UploadType[props.dataset ?? ""];

switch (uploadType) {
    case UploadType.contacts:
        store = useContactsStore();
        break;
    case UploadType.communityPolicing:
        store = useCommunityPolicingStore();
        break;
    case UploadType.nibrs:
        store = useNibrsStore();
        break;
    case UploadType.summary:
        store = useSummaryStore();
        break;
    case UploadType.useOfForce:
        store = useUofStore();
        break;    
    case UploadType.policeEmployee:
        store = usePoliceEmployeeStore();
        break;
    case UploadType.population:
        store = usePopulationStore();
        break;
    case UploadType.dcra:
        store = useDcraStore();
        break;
}

//What would have gone under data: {...}
var dataItems = ref([] as any[]);
const columns = store.getFileUploadColumns(props.onlyHasOneOri, props.submissionPeriodHidden);
var options = ref({
    totalItemCount: 0,
    itemsPerPage: 25,
    page: 1,
    sortBy: [
        {
            key: "uploadDate",
            isDescending: true,
        },
    ],
    pagingMode: props.noTotal ? "noTotal" : "withTotal",
} as BTableOptions);
var showLoading = ref(false);
var initialLoad = ref(true);

var isShowFilterEditor = ref(false);
var hasAppliedFilters = ref(false);
var filterOptions = ref({
    filters: [],
} as BFilterOptions);
var total = ref(0);
var updateTableDataSource = debounce(() => getData(), "10000ms");
var showDownloadErrorModal = ref(false);
var downloadErrorBody = ref("An error was encountered while trying to download the file you requested.");

var filtersDescription = ref("");

//Computed properties
const result = computed(() => {
    return dataItems.value;
});

var filterableFields = computed((): BFilterableField[] => {
    return store.getFilterableFieldsForFileUploads(
        props.onlyHasOneOri,
        props.submissionPeriodHidden
    ) as BFilterableField[];
});

function setDescription() {
    if (filterOptions == null) {
        return "";
    }
    hasAppliedFilters.value = filterOptions.value.filters.length > 0;

    return describeFilters(filterOptions.value.filters, filterableFields.value);
}

//Get values from query string
const params: Record<string, any> = new URLSearchParams(window.location.search);

if (params.has("pagenumber") || params.has("pagesize") || params.has("filter") || params.has("sort")) {
    let pagenumber = params.pagenumber; // "some_value"
    let pagesize = params.pagesize;
    let filters = params.filter;
    let sort = params.sort;

    if (pagenumber > 1) {
        options.value.page = parseInt(pagenumber);
    }

    if (pagesize != undefined) {
        options.value.itemsPerPage = parseInt(pagesize);
    }

    if (sort != undefined && sort.length > 0) {
        let isDescending = sort.toString().indexOf("-") === 0;
        let sortBy = [
            {
                key: sort.toString().substring(1),
                isDescending: isDescending,
            },
        ];
        options.value.sortBy = sortBy;
    }

    if (filters != undefined && filters.length > 0) {
        filterOptions.value = getFiltersFromString(filters);
    }
} else {
    //Saved table options
    let optionsJson = sessionStorage.getItem(`du_${uploadType}_tableOptions`);
    if (optionsJson) {
        options.value = JSON.parse(optionsJson) as BTableOptions;
    }

    //filters
    let filtersJson = sessionStorage.getItem(`du_${uploadType}_filterOptions`);
    if (filtersJson) {
        filterOptions.value = JSON.parse(filtersJson) as BFilterOptions;
    }
}

if (filterOptions.value.filters.length > 0) {
    applyFilters(filterOptions.value);
} else {
    getData();
}

initialLoad.value = false;

function getData(autoRefresh: boolean = false) {
    if (isShowFilterEditor.value) {
        return;
    }
    
    showLoading.value = true;
    options.value.pagingMode = props.noTotal ? "noTotal" : "withTotal";

    store?.getFiles(filterOptions.value.filters, options.value, filterableFields.value, options.value.sortBy)
        .then((response: AxiosResponse) => {
            if (options.value.pagingMode == "noTotal" && response?.data?.data?.length > 0) {
                //When were hiding the total text,first button, and last button then we return itemsPerPage + 1 in our get data call.
                //If the returned amount of rows is greater than itemsPerPage then enable the next button otherwise disable the next button to prevent an error
                dataItems.value = response.data.data.slice(0, options.value.itemsPerPage);
            } else {
                dataItems.value = response.data.data;
            }

            total.value = response.data.total;
            options.value.totalItemCount = response.data.total;
            if (dataItems.value.findIndex((item) => item.status === "Pending" || item.status == "In progress") >= 0) {
                autoRefresh = true;
            }
        })
        .catch(function (error) {
            handleErrorInResponse(error);
        })
        .finally(() => {
            showLoading.value = false;
            if (autoRefresh) {
                updateTableDataSource();
            }
        });
}

function applyFilters(newFilterOptions: BFilterOptions) {
    filterOptions.value = newFilterOptions;
    isShowFilterEditor.value = false;

    if (hasAppliedFilters.value) {
        options.value.page = 1;
    }

    hasAppliedFilters.value = filterOptions.value.filters.length > 0;
    filtersDescription.value = describeFilters(filterOptions.value.filters, filterableFields.value);

    getData();
}

function resetFilters() {
    if (filterOptions.value != null) {
        filterOptions.value.filters = [];
        options.value.page = 1;
    }

    isShowFilterEditor.value = false;
    hasAppliedFilters.value = false;
    filtersDescription.value = "";

    getData();
}

function showFilterEditor(event: any) {
    event.preventDefault();

    isShowFilterEditor.value = true;
}

function handleOptionChange(newOptions) {
    if (newOptions != null) {
        options.value = newOptions;
        getData();
    }
}

function getDetailsUrl(id: number): string {
    if (id > 0) {
        return `uploaddetails.aspx?id=${id}&source=${getSourceNumberFromText(props.dataset as string)}`;
    }
    return "";
}

function isStatusLinkEnabled(item: any): boolean {
    return item.id > 0;
}

function redirectToUrl(url: string) {
    window.location.href = url;
}

function getDownloadUrl(item: any) {
    if (!item.fileName || !item.downloadUrl) {
        return "";
    }
    return item.downloadUrl;
}

function handleDownloadErrorModalDismiss() {
    showDownloadErrorModal.value = false;
}

function downloadFileHopefully(downloadUrl: string, fileName: string) {
    try {
        checkIfDownloadValid(downloadUrl)
            .then((result) => {
                if (result) {
                    window.open(downloadUrl, "_blank");
                } else {
                    downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
                    showDownloadErrorModal.value = true;
                }
            })
            .catch((error) => {
                downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
                showDownloadErrorModal.value = true;
            });
    } catch (error) {
        downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
        showDownloadErrorModal.value = true;
    }
}

async function checkIfDownloadValid(downloadUrl: string): Promise<boolean> {
    let result = false;

    if (downloadUrl != null && downloadUrl != "") {
        result = await store.checkIfDownloadValid(downloadUrl);
    }

    return result;
}

watch(
    () => options,
    (newOptions) => {
        sessionStorage.setItem(`du_${uploadType}_tableOptions`, JSON.stringify(options.value));
        if (!initialLoad) getData();
    },
    {deep: true}
);

watch(
    () => filterOptions,
    (newFilters) => {
        sessionStorage.setItem(`du_${uploadType}_filterOptions`, JSON.stringify(filterOptions.value));
    },
    {deep: true}
);

watch(
    () => props.refreshRequired,
    (val) => {
        getData(true);
    }
);
</script>
